'use client';
import { useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { copyReleaseVersion } from '@/sections/develocity/ReleaseVersionSection/releaseVersionSection.copy';
import { useGSAP } from '@gsap/react';

export const ReleaseVersionSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const { info, title, subtitle, image, link } = copyReleaseVersion;

  useGSAP(
    () => {
      gsap.registerPlugin(ScrollTrigger);

      const { current } = ref;
      if (!current) return;

      const uncover = gsap.timeline({ paused: true });
      gsap.set(current, { yPercent: -100 });
      uncover.to(current, {
        yPercent: 0,
        ease: 'none',
        duration: 1,
      });

      const end = current.offsetHeight;

      ScrollTrigger.create({
        trigger: current,
        start: 'bottom bottom',
        end: `+=${end} bottom`,
        scrub: true,
        animation: uncover,
        markers: false,
      });
    },
    { scope: ref },
  );

  return (
    <div className="relative py-14 will-change-transform" ref={ref}>
      <div className="container flex flex-col lg:flex-row">
        <div className="flex w-full items-center justify-center rounded-bl-none rounded-tl-2xl rounded-tr-2xl bg-light-background-200 p-8 md:rounded-bl-none md:rounded-tr-2xl lg:w-1/2 lg:rounded-bl-2xl lg:rounded-tr-none">
          <Image src={image.src} alt={image.alt} width={image.width} height={image.height} />
        </div>
        <div className="text-body flex w-full flex-col items-start rounded-bl-2xl rounded-br-2xl rounded-tr-none bg-light-background-100 p-8 md:rounded-tr-2xl lg:w-1/2 lg:rounded-bl-none">
          <span className="text-caption mb-4 rounded-lg bg-gradient-to-br from-primary to-secondary px-2 py-1 text-white">
            {info}
          </span>
          <h4 className="text-h5 mb-3 text-gray-1">{title}</h4>
          <p className="mb-10 flex flex-grow text-gray-3 lg:mb-0">{subtitle}</p>
          <Link
            href={link.href}
            className="underline-text-transition-blue group flex items-center justify-center whitespace-nowrap text-gray-1"
          >
            <span>{link.text}</span> <ChevronRightIcon className="inline-block size-6 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </div>
  );
};
